import { FIRST_DAYS } from 'components/calendar/CalendarView'
import { getDefaultTimezone, setDefaultTimezone } from 'index'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import eventBus, { AppEvents, EventData } from 'services/EventBus'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { isPreviewMode } from 'utils/user'

interface AppSettingsProviderProps {
  children: ReactNode
}

interface AppSettingsContextProps {
  firstDayOfTheWeek: number
  setFirstDayOfTheWeek: (n: number) => void
  timeZone: string
  setTimeZone: (tz: string) => void
  themeMode: AppThemeMode
  setThemeMode: (mode: AppThemeMode) => void
  resolvedThemeMode: ResolvedThemeMode
  isErrorDialogOpen: boolean
  errorMessage?: string
  errorTitle?: string
  closeErrorDialog: () => void
}

export const APP_THEME_MODES = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark'
} as const

export type AppThemeMode =
  (typeof APP_THEME_MODES)[keyof typeof APP_THEME_MODES]
export type ResolvedThemeMode = Exclude<
  AppThemeMode,
  typeof APP_THEME_MODES.SYSTEM
>

function isValidThemeMode(
  mode: string | null | undefined
): mode is AppThemeMode {
  return (
    mode === APP_THEME_MODES.SYSTEM ||
    mode === APP_THEME_MODES.LIGHT ||
    mode === APP_THEME_MODES.DARK
  )
}

function getPreviewThemeModeOverride(): AppThemeMode | undefined {
  const urlParams = new URLSearchParams(window.location.search)

  if (!isPreviewMode()) {
    return undefined
  }

  const themeMode = urlParams.get('themeMode')

  if (isValidThemeMode(themeMode)) {
    return themeMode
  }

  return undefined
}

function getStoredThemeMode(): AppThemeMode {
  const mode = LocalStorage.get(LocalStorageKey.THEME_MODE)

  if (isValidThemeMode(mode)) {
    return mode
  }

  return APP_THEME_MODES.SYSTEM
}

function getSystemResolvedTheme(): ResolvedThemeMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? APP_THEME_MODES.DARK
    : APP_THEME_MODES.LIGHT
}

function resolveThemeMode(mode: AppThemeMode): ResolvedThemeMode {
  if (mode === APP_THEME_MODES.SYSTEM) {
    return getSystemResolvedTheme()
  }

  return mode
}

export default function AppSettingsProvider(props: AppSettingsProviderProps) {
  const [firstDayOfTheWeek, setFirstDayOfTheWeek] = useState(FIRST_DAYS.SUNDAY)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  const [errorTitle, setErrorTitle] = useState<string | undefined>(undefined)
  const [timeZone, setTimeZone] = useState(getDefaultTimezone())
  const [themeMode, setThemeMode] = useState<AppThemeMode>(getStoredThemeMode)
  const previewThemeModeOverride = getPreviewThemeModeOverride()
  const effectiveThemeMode = previewThemeModeOverride ?? themeMode
  const [resolvedThemeMode, setResolvedThemeMode] = useState<ResolvedThemeMode>(
    () => resolveThemeMode(previewThemeModeOverride ?? getStoredThemeMode())
  )

  useEffect(() => {
    setDefaultTimezone(timeZone)
    Logger.pink('APP SETTINGS:TZ', getDefaultTimezone())
  }, [timeZone])

  useEffect(() => {
    LocalStorage.set(LocalStorageKey.THEME_MODE, themeMode)
  }, [themeMode])

  useLayoutEffect(() => {
    // Update DOM attributes BEFORE calling setResolvedThemeMode so that
    // defaultGroupImage() reads the correct theme during the triggered re-render.
    const resolved = resolveThemeMode(effectiveThemeMode)
    document.documentElement.style.colorScheme = resolved
    document.documentElement.setAttribute('data-theme', resolved)
    setResolvedThemeMode(resolved)
  }, [effectiveThemeMode])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const onSystemThemeChange = () => {
      if (effectiveThemeMode === APP_THEME_MODES.SYSTEM) {
        const resolved = getSystemResolvedTheme()
        // Update DOM before state change for the same timing reason as above.
        document.documentElement.style.colorScheme = resolved
        document.documentElement.setAttribute('data-theme', resolved)
        setResolvedThemeMode(resolved)
      }
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onSystemThemeChange)
    } else {
      mediaQuery.addListener(onSystemThemeChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', onSystemThemeChange)
      } else {
        mediaQuery.removeListener(onSystemThemeChange)
      }
    }
  }, [effectiveThemeMode])

  useLayoutEffect(() => {
    document.documentElement.style.colorScheme = resolvedThemeMode
    document.documentElement.setAttribute('data-theme', resolvedThemeMode)
    document.documentElement.style.setProperty(
      '--app-accent-color',
      resolvedThemeMode === APP_THEME_MODES.DARK
        ? isGroupcalApp()
          ? '#ff4f71'
          : '#ff0032'
        : APP_COLOR
    )
    document.documentElement.style.setProperty(
      '--app-selected-color',
      resolvedThemeMode === APP_THEME_MODES.DARK
        ? 'white'
        : is24meApp()
          ? 'black'
          : 'white'
    )
  }, [resolvedThemeMode])

  useEffect(() => {
    const callback = (event: EventData) => {
      if (event.name === AppEvents.EventItemError) {
        setIsErrorDialogOpen(true)
        setErrorMessage(event.data.message)
        setErrorTitle(event.data.title)
      }
    }

    eventBus.subscribe(AppEvents.EventItemError, callback)

    return () => {
      eventBus.unsubscribe(AppEvents.EventItemError, callback)
    }
  }, [])

  useEffect(() => {
    // Check for query parameter first
    const urlParams = new URLSearchParams(window.location.search)
    const queryFirstDay = urlParams.get('firstDayOfWeek')

    if (queryFirstDay) {
      const dayValue = Number(queryFirstDay)
      if (
        [FIRST_DAYS.MONDAY, FIRST_DAYS.SUNDAY, FIRST_DAYS.SATURDAY].includes(
          dayValue
        )
      ) {
        setFirstDayOfTheWeek(dayValue)
        return
      }
    }

    // Otherwise use user settings
    if (LocalStorage.get(LocalStorageKey.USER_ID)) {
      UserSettings().then((settings) => {
        if (!settings || settings === null) {
          setFirstDayOfTheWeek(FIRST_DAYS.SUNDAY)
        } else {
          setFirstDayOfTheWeek(
            settings &&
              [
                FIRST_DAYS.MONDAY,
                FIRST_DAYS.SUNDAY,
                FIRST_DAYS.SATURDAY
              ].includes(settings?.FirstDayOfTheWeek)
              ? settings?.FirstDayOfTheWeek
              : FIRST_DAYS.SUNDAY
          )
        }
      })
    } else {
      setFirstDayOfTheWeek(FIRST_DAYS.SUNDAY)
    }
  }, [])

  const context: AppSettingsContextProps = {
    firstDayOfTheWeek,
    setFirstDayOfTheWeek,
    timeZone,
    setTimeZone,
    themeMode: effectiveThemeMode,
    setThemeMode,
    resolvedThemeMode,
    isErrorDialogOpen,
    errorMessage,
    errorTitle,
    closeErrorDialog: () => setIsErrorDialogOpen(false)
  }

  window.setTimeZone = setTimeZone

  return (
    <AppSettingsContext.Provider value={context}>
      {props.children}
    </AppSettingsContext.Provider>
  )
}

const AppSettingsContext = createContext<AppSettingsContextProps>({
  firstDayOfTheWeek: 1,
  setFirstDayOfTheWeek: () => {},
  timeZone: 'UTC',
  setTimeZone: () => {},
  themeMode: APP_THEME_MODES.SYSTEM,
  setThemeMode: () => {},
  resolvedThemeMode: APP_THEME_MODES.LIGHT,
  isErrorDialogOpen: false,
  errorMessage: undefined,
  errorTitle: undefined,
  closeErrorDialog: () => {}
})

export function useAppSettings(): AppSettingsContextProps {
  return useContext(AppSettingsContext)
}
